import React, { useState } from 'react';
import { FaMapMarkerAlt, FaCalendarAlt, FaClock, FaCheckCircle, FaGlobe, FaCar, FaHandshake, FaStar, FaQuoteLeft, FaPhone, FaEnvelope, FaArrowRight } from 'react-icons/fa';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';

export default function Rental() {
  const [pickupLocation, setPickupLocation] = useState('');
  const [returnLocation, setReturnLocation] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [returnTime, setReturnTime] = useState('');
  const [differentReturn, setDifferentReturn] = useState(false);

  const benefits = [
    {
      icon: FaGlobe,
      title: "Wide Coverage",
      description: "Multiple locations across Africa with convenient pickup and drop-off points.",
      color: "from-blue-500 to-blue-700"
    },
    {
      icon: FaCar,
      title: "Premium Fleet",
      description: "From luxury sedans to spacious SUVs, choose from our extensive vehicle selection.",
      color: "from-[#DC2626] to-[#B91C1C]"
    },
    {
      icon: FaHandshake,
      title: "Trusted Service",
      description: "Stress-free rental experience with transparent pricing and no hidden costs.",
      color: "from-green-500 to-green-700"
    }
  ];

  const features = [
    {
      title: "Hourly Rentals",
      subtitle: "Flexible Short-Term Options",
      description: "Rent by the hour for meetings, events, or quick trips. Perfect for when you need a car for just a few hours.",
      image: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800&q=80",
      badge: "Starting from $50/hr",
      buttonText: "Book Now",
      color: "from-blue-600 to-blue-800"
    },
    {
      title: "Daily Rentals",
      subtitle: "Extended Rental Plans",
      description: "Get the best value with our daily rental packages. Ideal for weekend trips or extended business travel.",
      image: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=800&q=80",
      badge: "15% Discount",
      buttonText: "Book Now",
      color: "from-[#DC2626] to-[#B91C1C]"
    },
    {
      title: "Premium Vehicles",
      subtitle: "Luxury & Comfort",
      description: "Experience premium comfort with our luxury vehicle collection. Perfect for special occasions or business travel.",
      image: "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=800&q=80",
      badge: "Premium Fleet",
      buttonText: "Explore",
      color: "from-purple-600 to-purple-800"
    }
  ];

  const locations = [
    "Lagos Airport (LOS)", "Abuja Airport (ABV)", "Port Harcourt Airport (PHC)",
    "Kano Airport (KAN)", "Lagos Island", "Victoria Island",
    "Ikoyi", "Ikeja", "Lekki", "Garki", "Wuse", "Maitama"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section with Booking Widget */}
      <div className="relative bg-gradient-to-br from-slate-900 via-gray-900 to-black pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40"></div>
        
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          {/* Hero Text */}
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-tight">
              Don&apos;t rent a car.
              <br />
              <span className="text-[#DC2626]">Rent the car.</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 font-medium max-w-3xl mx-auto">
              Premium car rental at affordable rates. Across Africa.
            </p>
          </div>

          {/* Booking Widget */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10 max-w-5xl mx-auto border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Pickup Location */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Pickup Location</label>
                <div className="relative">
                  <FaMapMarkerAlt className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg z-10" />
                  <Input
                    type="text"
                    placeholder="Airport, city or address"
                    value={pickupLocation}
                    onChange={(e) => setPickupLocation(e.target.value)}
                    className="pl-12"
                    size="lg"
                  />
                </div>
              </div>

              {/* Return Location */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Return Location</label>
                <div className="relative">
                  <FaMapMarkerAlt className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg z-10" />
                  <Input
                    type="text"
                    placeholder={differentReturn ? "Airport, city or address" : "Same as pickup"}
                    value={returnLocation}
                    onChange={(e) => setReturnLocation(e.target.value)}
                    disabled={!differentReturn}
                    className="pl-12"
                    size="lg"
                  />
                </div>
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={differentReturn}
                    onChange={(e) => setDifferentReturn(e.target.checked)}
                    className="w-4 h-4 text-[#DC2626] rounded border-gray-300 focus:ring-[#DC2626]"
                  />
                  <span className="text-sm text-gray-700">Different return location</span>
                </label>
              </div>

              {/* Pickup Date & Time */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Pickup Date & Time</label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" />
                    <Input
                      type="date"
                      value={pickupDate}
                      onChange={(e) => setPickupDate(e.target.value)}
                      className="pl-10"
                      size="lg"
                    />
                  </div>
                  <div className="relative">
                    <FaClock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" />
                    <Input
                      type="time"
                      value={pickupTime}
                      onChange={(e) => setPickupTime(e.target.value)}
                      className="pl-10"
                      size="lg"
                    />
                  </div>
                </div>
              </div>

              {/* Return Date & Time */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Return Date & Time</label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" />
                    <Input
                      type="date"
                      value={returnDate}
                      onChange={(e) => setReturnDate(e.target.value)}
                      className="pl-10"
                      size="lg"
                    />
                  </div>
                  <div className="relative">
                    <FaClock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" />
                    <Input
                      type="time"
                      value={returnTime}
                      onChange={(e) => setReturnTime(e.target.value)}
                      className="pl-10"
                      size="lg"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <Button
                className="w-full bg-gradient-to-r from-[#DC2626] to-[#B91C1C] hover:from-[#B91C1C] hover:to-[#991B1B] text-white font-bold py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
                size="lg"
              >
                Show Available Cars
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20 relative z-10">
        {/* Key Benefits Section */}
        <div className="mb-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="text-center group">
                  <div className={`w-20 h-20 bg-gradient-to-br ${benefit.color} rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    <IconComponent className="text-white text-4xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-700 text-lg leading-relaxed font-medium">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Feature Cards Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-gradient-to-r from-red-50 to-orange-50 rounded-full border border-red-100">
              <span className="w-2 h-2 bg-[#DC2626] rounded-full animate-pulse"></span>
              <span className="text-sm font-bold text-[#DC2626] uppercase tracking-wider">
                Rental Options
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6">
              <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                Choose Your
              </span>
              <br />
              <span className="bg-gradient-to-r from-[#DC2626] via-[#B91C1C] to-[#991B1B] bg-clip-text text-transparent">
                Rental Plan
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="relative bg-gradient-to-br from-gray-900 to-black rounded-3xl overflow-hidden shadow-2xl group hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)] transition-all duration-300 transform hover:scale-[1.02]"
              >
                <div
                  className="absolute inset-0 bg-cover bg-center opacity-40 group-hover:opacity-50 transition-opacity duration-300"
                  style={{ backgroundImage: `url(${feature.image})` }}
                ></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-transparent"></div>
                
                <div className="relative z-10 p-8 md:p-10 text-white h-full flex flex-col justify-between min-h-[400px]">
                  <div>
                    {feature.badge && (
                      <div className={`inline-block px-4 py-2 bg-gradient-to-r ${feature.color} rounded-full mb-4 shadow-lg`}>
                        <span className="text-sm font-bold text-white">{feature.badge}</span>
                      </div>
                    )}
                    <h3 className="text-3xl md:text-4xl font-bold mb-3">{feature.title}</h3>
                    <p className="text-lg font-semibold text-gray-300 mb-4">{feature.subtitle}</p>
                    <p className="text-gray-300 leading-relaxed mb-6">{feature.description}</p>
                  </div>
                  <Button
                    className={`w-full bg-gradient-to-r ${feature.color} hover:opacity-90 text-white font-bold py-4 text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2`}
                  >
                    {feature.buttonText}
                    <FaArrowRight />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonial Section */}
        <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-20">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1200&q=80)' }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900"></div>
          
          <div className="relative z-10 p-12 md:p-20 text-center">
            <FaQuoteLeft className="text-[#DC2626] text-5xl md:text-6xl mb-8 mx-auto" />
            <blockquote className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-8 max-w-4xl mx-auto leading-relaxed">
              &quot;GIGA Mobility is the only rental company I&apos;ve never had an issue with. Seriously... they&apos;ve turned me into a lifelong client.&quot;
            </blockquote>
            <p className="text-xl text-gray-300 font-semibold">- K. Adebayo, Lagos</p>
          </div>
        </div>

        {/* Locations Section */}
        <div className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
                <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                  Drive First Class.
                </span>
                <br />
                <span className="bg-gradient-to-r from-[#DC2626] via-[#B91C1C] to-[#991B1B] bg-clip-text text-transparent">
                  Pay Economy.
                </span>
              </h2>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6 font-medium">
                Premium vehicles without premium prices. Our fleet includes luxury sedans, SUVs, and premium cars from top manufacturers, all at affordable rates.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md border border-gray-200">
                  <FaCheckCircle className="text-green-600" />
                  <span className="font-semibold text-gray-700">Premium Fleet</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md border border-gray-200">
                  <FaCheckCircle className="text-green-600" />
                  <span className="font-semibold text-gray-700">Affordable Rates</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md border border-gray-200">
                  <FaCheckCircle className="text-green-600" />
                  <span className="font-semibold text-gray-700">Multiple Locations</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Popular Locations</h3>
              <div className="grid grid-cols-2 gap-4">
                {locations.map((location, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group"
                  >
                    <FaMapMarkerAlt className="text-[#DC2626] group-hover:scale-110 transition-transform" />
                    <span className="text-gray-700 font-medium group-hover:text-[#DC2626] transition-colors">{location}</span>
                  </div>
                ))}
              </div>
              <Button
                className="w-full mt-6 bg-gray-900 hover:bg-gray-800 text-white font-semibold py-3 rounded-xl"
              >
                View All Locations
              </Button>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-[#DC2626] via-[#C91C1C] to-[#B91C1C] rounded-3xl shadow-2xl p-12 md:p-20 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -ml-48 -mb-48"></div>
          
          <div className="relative z-10 text-center">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6">
              Ready to Rent Your Perfect Car?
            </h2>
            <p className="text-xl md:text-2xl text-white/95 mb-10 max-w-3xl mx-auto font-medium leading-relaxed">
              Experience premium car rental service with transparent pricing and exceptional customer care.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button
                className="px-10 py-5 bg-white text-[#DC2626] font-bold text-lg rounded-xl hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-xl !text-[#DC2626]"
                size="lg"
              >
                Start Booking
              </Button>
              <Button
                className="px-10 py-5 bg-white/10 backdrop-blur-md text-white font-bold text-lg rounded-xl hover:bg-white/20 transition-all duration-300 border-2 border-white/30 transform hover:scale-105 shadow-xl !text-white"
                size="lg"
                variant="bordered"
              >
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
